/**
 * Calendar Feed Token Management
 * 
 * Manages secure tokens for ICS feed access.
 * Tokens are used to protect calendar feeds without requiring OAuth.
 */

import { collection, doc, setDoc, getDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

export interface CalendarFeedToken {
  userId: string;
  calendarId: string;
  token: string;
  expiresAt?: Date;
  createdAt: Date;
}

/**
 * Generate a secure random token for calendar feed access.
 */
function generateSecureToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Create or regenerate a calendar feed token.
 * Returns the token and feed URL.
 */
export async function createCalendarFeedToken(
  userId: string,
  calendarId: string,
  expiresInDays?: number
): Promise<{ token: string; feedUrl: string }> {
  try {
    const token = generateSecureToken();
    const now = new Date();
    
    const tokenData: Omit<CalendarFeedToken, 'expiresAt'> & { expiresAt?: Date } = {
      userId,
      calendarId,
      token,
      createdAt: now,
    };

    if (expiresInDays) {
      const expiresAt = new Date(now);
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);
      tokenData.expiresAt = expiresAt;
    }

    const tokenDocId = `${userId}_${calendarId}`;
    const firestoreData: any = {
      userId: tokenData.userId,
      calendarId: tokenData.calendarId,
      token: tokenData.token,
      createdAt: Timestamp.fromDate(tokenData.createdAt),
    };
    
    if (tokenData.expiresAt) {
      firestoreData.expiresAt = Timestamp.fromDate(tokenData.expiresAt);
    }
    
    await setDoc(doc(db, 'calendarFeedTokens', tokenDocId), firestoreData);

    // Build feed URL
    const baseUrl = window.location.origin;
    const feedUrl = `${baseUrl}/calendar-feed/${userId}/${calendarId}/${token}`;

    return { token, feedUrl };
  } catch (error) {
    console.error('[createCalendarFeedToken] Error:', error);
    throw error;
  }
}

/**
 * Get existing calendar feed token and URL.
 */
export async function getCalendarFeedToken(
  userId: string,
  calendarId: string
): Promise<{ token: string; feedUrl: string } | null> {
  try {
    const tokenDocId = `${userId}_${calendarId}`;
    const tokenDoc = await getDoc(doc(db, 'calendarFeedTokens', tokenDocId));

    if (!tokenDoc.exists()) {
      return null;
    }

    const data = tokenDoc.data() as CalendarFeedToken & { expiresAt?: Timestamp };

    // Check if expired
    if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
      return null;
    }

    const baseUrl = window.location.origin;
    const feedUrl = `${baseUrl}/calendar-feed/${userId}/${calendarId}/${data.token}`;

    return { token: data.token, feedUrl };
  } catch (error) {
    console.error('[getCalendarFeedToken] Error:', error);
    return null;
  }
}

/**
 * Revoke a calendar feed token.
 */
export async function revokeCalendarFeedToken(
  userId: string,
  calendarId: string
): Promise<void> {
  try {
    const tokenDocId = `${userId}_${calendarId}`;
    await deleteDoc(doc(db, 'calendarFeedTokens', tokenDocId));
  } catch (error) {
    console.error('[revokeCalendarFeedToken] Error:', error);
    throw error;
  }
}

/**
 * Regenerate a calendar feed token by revoking the existing one and issuing a fresh token.
 */
export async function regenerateCalendarFeedToken(
  userId: string,
  calendarId: string,
  expiresInDays?: number
): Promise<{ token: string; feedUrl: string }> {
  await revokeCalendarFeedToken(userId, calendarId);
  return createCalendarFeedToken(userId, calendarId, expiresInDays);
}

/**
 * Get or create a calendar feed token.
 * If a valid token exists, return it. Otherwise, create a new one.
 */
export async function getOrCreateCalendarFeedToken(
  userId: string,
  calendarId: string,
  expiresInDays?: number
): Promise<{ token: string; feedUrl: string }> {
  const existing = await getCalendarFeedToken(userId, calendarId);
  if (existing) {
    return existing;
  }
  return createCalendarFeedToken(userId, calendarId, expiresInDays);
}
